import {
  ADOPTION_MAX_LIMIT,
  ComparisonLineItem,
  ComparisonRow,
  ComparisonWidget,
  GroupByDimension,
} from '../services/comparisonDashboardService';
import { COMPARISON_METRIC_COLUMNS } from '../components/reports/comparisonBreakdownConstants';
import {
  aggregate_comparison_rows,
  breakdown_item_number,
  comparison_secondary_dimension,
  ROLLUP_HIDDEN_METRIC_KEYS,
} from './comparisonBreakdownDrilldown';

function csv_cell(value: string | number): string {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
}

function csv_line(cells: Array<string | number>): string {
  return cells.map(csv_cell).join(',');
}

function metric_cells(row: ComparisonRow, hide_averages = false): Array<string | number> {
  return COMPARISON_METRIC_COLUMNS.map((col) => {
    if (
      hide_averages &&
      ROLLUP_HIDDEN_METRIC_KEYS.includes(col.key as (typeof ROLLUP_HIDDEN_METRIC_KEYS)[number])
    ) {
      return '';
    }
    return (row as unknown as Record<string, number>)[col.key] ?? 0;
  });
}

const LIST_HEADERS = ['Item #', 'Name', ...COMPARISON_METRIC_COLUMNS.map((c) => c.label)];

const DETAIL_HEADERS = [
  'Group Item #',
  'Group Name',
  'Item #',
  'Name',
  ...COMPARISON_METRIC_COLUMNS.map((c) => c.label),
];

const LINE_HEADERS = [
  'Date',
  'Invoice #',
  'Customer #',
  'Customer',
  'Item #',
  'Name',
  'Qty',
  'Revenue',
  'Unit Price',
];

export function breakdown_list_csv(rows: ComparisonRow[], dimension: GroupByDimension): string {
  const lines = [csv_line(LIST_HEADERS)];
  for (const row of rows) {
    lines.push(
      csv_line([breakdown_item_number(dimension, row.row_key), row.row_label, ...metric_cells(row)])
    );
  }
  return lines.join('\n');
}

/** Group totals plus every child row so a download shows what makes up each number. */
export function breakdown_composition_csv(
  widgets: ComparisonWidget[],
  group_by: GroupByDimension
): string {
  const secondary = comparison_secondary_dimension(group_by);
  const lines = [csv_line(['Summary']), csv_line(LIST_HEADERS)];
  for (const widget of widgets) {
    const totals: ComparisonRow = {
      row_key: widget.group_key,
      row_label: widget.group_label,
      ...aggregate_comparison_rows(widget.rows),
    };
    lines.push(
      csv_line([
        breakdown_item_number(group_by, totals.row_key),
        totals.row_label,
        ...metric_cells(totals, true),
      ])
    );
  }
  lines.push('');
  lines.push(csv_line(['Detail']));
  lines.push(csv_line(DETAIL_HEADERS));
  for (const widget of widgets) {
    for (const row of widget.rows) {
      lines.push(
        csv_line([
          breakdown_item_number(group_by, widget.group_key),
          widget.group_label,
          breakdown_item_number(secondary, row.row_key),
          row.row_label,
          ...metric_cells(row),
        ])
      );
    }
  }
  return lines.join('\n');
}

export function line_items_csv(rows: ComparisonLineItem[], truncated = false): string {
  const lines: string[] = [];
  if (truncated) {
    lines.push(
      csv_line(['Showing the first 500 invoice lines. Totals on this sheet may be short.'])
    );
  }
  lines.push(csv_line(LINE_HEADERS));
  for (const row of rows) {
    lines.push(
      csv_line([
        row.date,
        row.invoice_number,
        row.customer_number,
        row.customer_name,
        row.item_number,
        row.item_name,
        row.qty,
        row.revenue,
        row.unit_price,
      ])
    );
  }
  return lines.join('\n');
}

export function adoption_customers_csv(
  bought: ComparisonRow[],
  not_bought: ComparisonRow[],
  truncated = false
): string {
  const lines: string[] = [];
  if (truncated) {
    lines.push(
      csv_line([
        `Showing the first ${ADOPTION_MAX_LIMIT} customers per list. Totals on this sheet may be short.`,
      ])
    );
  }
  lines.push(csv_line(['Status', ...LIST_HEADERS, 'Total Sales', 'Orders']));
  for (const row of bought) {
    lines.push(
      csv_line([
        'Bought',
        breakdown_item_number('customer', row.row_key),
        row.row_label,
        ...metric_cells(row),
        row.customer_total_sales ?? 0,
        row.customer_order_count ?? 0,
      ])
    );
  }
  for (const row of not_bought) {
    lines.push(
      csv_line([
        'Did not buy',
        breakdown_item_number('customer', row.row_key),
        row.row_label,
        ...metric_cells(row),
        row.customer_total_sales ?? 0,
        row.customer_order_count ?? 0,
      ])
    );
  }
  return lines.join('\n');
}

export function download_csv(filename: string, csv: string): void {
  const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
